import React from "react";
import { IonApp, IonRouterLink, IonRouterOutlet } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import MainApp from "./routes/routes";
import toast, { Toaster } from "react-hot-toast";

import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  concat,
} from "@apollo/client";
import { asyncMap } from "@apollo/client/utilities";
import { createUploadLink } from "apollo-upload-client";

import { API_URL, JWT_TOKEN } from "./constants/appConfig";
import { AuthProvider } from "./context/AuthContext";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";
import "./theme/common.utils.css";
import { getLocalSession, getLocalStorage } from "./lib/utils/storage.utils";
import { logout } from "./lib/utils/auth.utils";

const httpLink = createUploadLink({
  uri: `${API_URL}/graphql`,
});

const authMiddleware = new ApolloLink((operation, forward) => {
  const token = getLocalStorage(JWT_TOKEN) || getLocalSession(JWT_TOKEN);
  // add the authorization to the headers
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      "x-auth-token": token ? `${token}` : "",
    },
  }));

  return asyncMap(forward(operation), async (response: any) => {
    const data: any = response.data;
    const errors: any = response.errors;

    if (errors && errors?.length > 0) {
      if (
        errors?.[0]?.message ===
        "Per completare l'ordine è necessario completare le informazioni sull'azienda e sulla fatturazione."
      ) {
        toast.error(
          <div>
            <p>{errors[0].message}</p>
            <span>
              Completa il tuo profilo!
              <IonRouterLink href="/personale/dati"> Clicca qui</IonRouterLink>
            </span>
          </div>
        );
      } else {
        toast.error(errors?.[0]?.message);
        if (
          errors?.[0]?.message === "Invalid Token" ||
          errors?.[0]?.message ===
            "Si è verificato un errore. Riprovare più tardi."
        ) {
          // ErrorMessage(errors[0].message);
          logout();
          toast.error("Si è verificato un errore. Riprovare più tardi.");
        }
      }
    }

    if (data) {
      const responseDataQueryName = Object.keys(data)[0];
      const responseErrorMessage =
        data?.[responseDataQueryName]?.errors?.[0]?.message;

      if (responseErrorMessage) {
        if (
          responseErrorMessage === "Il tuo account è disabilitato" ||
          responseErrorMessage === "Your account is disabled" ||
          responseErrorMessage === "User is not verified" ||
          responseErrorMessage === ""
        ) {
          if (responseErrorMessage === "User is not verified") {
            toast.error(
              "Si prega di verificare l'e-mail per effettuare il login"
            );
          } else {
            logout();
            toast.error(responseErrorMessage);
          }
        }
      }
    }

    return response;
  });
});

const client = new ApolloClient({
  link: concat(authMiddleware, httpLink),
  cache: new InMemoryCache(),
});

const App: React.FC = () => (
  <IonApp>
    <Toaster position="top-right" reverseOrder={false} />

    <ApolloProvider client={client}>
      <IonReactRouter>
        <IonRouterOutlet>
          <AuthProvider>
            <MainApp />
          </AuthProvider>
        </IonRouterOutlet>
      </IonReactRouter>
    </ApolloProvider>
  </IonApp>
);

export default App;
